import { useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, ImageOff, Loader2, Play, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ChatwootAttachment } from '@/api/endpoints/support';
import { AudioPlayer } from '../AudioPlayer';
import { baixarMidia } from './baixar';
import { infoDoArquivo } from './arquivo';

interface Props {
  att: ChatwootAttachment;
  outgoing: boolean;
  messageId: number;
  /** Abre no visualizador em tela cheia (imagem, vídeo, PDF). */
  onAbrir?: () => void;
}

function Aviso({
  icone,
  texto,
  outgoing,
}: {
  icone: React.ReactNode;
  texto: string;
  outgoing: boolean;
}) {
  return (
    <span
      className={cn(
        'flex w-[260px] max-w-full items-center gap-2 rounded-xl px-3 py-2.5 text-xs',
        outgoing ? 'bg-white/10 text-white/85' : 'bg-card text-text-secondary'
      )}
    >
      {icone}
      <span className="min-w-0 flex-1">{texto}</span>
    </span>
  );
}

// Foto: miniatura de tamanho fixo (sem esticar a bolha), clique abre em tela cheia.
function Imagem({ url, nome, onAbrir }: { url: string; nome: string; onAbrir?: () => void }) {
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [tentativa, setTentativa] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  // Imagem que veio do cache termina de carregar antes do React ligar o
  // onLoad: sem esta checagem a miniatura ficava presa no esqueleto.
  useEffect(() => {
    const el = imgRef.current;
    if (el?.complete) setEstado(el.naturalWidth > 0 ? 'ok' : 'erro');
  }, [tentativa, url]);
  if (estado === 'erro') {
    return (
      <span className="flex h-40 w-[260px] max-w-full flex-col items-center justify-center gap-2 rounded-xl bg-card text-xs text-text-secondary">
        <ImageOff size={20} />A imagem não carregou.
        <button
          type="button"
          onClick={() => {
            setEstado('carregando');
            setTentativa((t) => t + 1);
          }}
          className="flex items-center gap-1 font-semibold text-primary hover:underline"
        >
          <RotateCcw size={12} /> Tentar de novo
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-label={`Ver ${nome} em tela cheia`}
      className="group relative block overflow-hidden rounded-xl bg-card"
    >
      {estado === 'carregando' && (
        <span className="block h-48 w-[260px] max-w-full animate-pulse bg-elevated" />
      )}
      <img
        ref={imgRef}
        key={tentativa}
        src={url}
        alt={nome}
        loading="lazy"
        onLoad={() => setEstado('ok')}
        onError={() => setEstado('erro')}
        className={cn(
          'block max-h-[300px] w-[260px] max-w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]',
          estado === 'carregando' && 'absolute inset-0 opacity-0'
        )}
      />
    </button>
  );
}

// Vídeo: primeiro quadro como capa + botão de play; toca em tela cheia.
function Video({ url, nome, onAbrir }: { url: string; nome: string; onAbrir?: () => void }) {
  const [duracao, setDuracao] = useState<number | null>(null);
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-label={`Assistir ${nome}`}
      className="group relative block overflow-hidden rounded-xl bg-black"
    >
      <video
        src={`${url}#t=0.1`}
        preload="metadata"
        muted
        playsInline
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) setDuracao(d);
        }}
        className="block max-h-[300px] w-[260px] max-w-full object-cover"
      />
      <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35">
        <span className="flex size-12 items-center justify-center rounded-full bg-white/90 text-black shadow-lg">
          <Play size={20} fill="currentColor" className="ml-0.5" />
        </span>
      </span>
      {duracao != null && (
        <span className="absolute bottom-2 right-2 rounded-md bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white">
          {Math.floor(duracao / 60)}:{String(Math.floor(duracao % 60)).padStart(2, '0')}
        </span>
      )}
    </button>
  );
}

// Documento: ícone por tipo, nome, "PDF · 12 KB" e as ações Visualizar/Baixar.
function Documento({ att, outgoing, messageId, onAbrir }: Props) {
  const info = infoDoArquivo(att);
  const [baixando, setBaixando] = useState(false);
  const url = att.data_url as string;
  const meta = [info.rotulo, info.tamanho].filter(Boolean).join(' · ');

  const baixar = async () => {
    setBaixando(true);
    await baixarMidia(messageId, info.nome, url);
    setBaixando(false);
  };

  return (
    <div
      className={cn(
        'w-[280px] max-w-full overflow-hidden rounded-xl',
        // Fundo sólido também na bolha azul: texto branco sobre o azul claro
        // ficava ilegível.
        'bg-card text-text-title',
        outgoing && 'shadow-sm'
      )}
    >
      <button
        type="button"
        onClick={info.visualizavel ? onAbrir : baixar}
        className="flex w-full items-center gap-3 px-3 py-3 text-left"
        title={info.visualizavel ? 'Visualizar' : 'Baixar'}
      >
        <span
          className={cn(
            'flex size-11 shrink-0 flex-col items-center justify-center rounded-lg',
            info.cor
          )}
        >
          <FileText size={18} />
          <span className="mt-0.5 text-[8px] font-bold leading-none">
            {info.rotulo.slice(0, 4)}
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'line-clamp-2 break-all text-[13px] font-semibold leading-snug',
              'text-text-title'
            )}
          >
            {info.nome}
          </span>
          {meta && (
            <span
              className={cn(
                'mt-0.5 block text-[11px]',
                outgoing ? 'text-white/70' : 'text-text-tertiary'
              )}
            >
              {meta}
            </span>
          )}
        </span>
      </button>
      <div className="flex border-t border-border">
        {info.visualizavel && (
          <button
            type="button"
            onClick={onAbrir}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors',
              'text-primary hover:bg-muted'
            )}
          >
            <Eye size={14} /> Visualizar
          </button>
        )}
        <button
          type="button"
          onClick={baixar}
          disabled={baixando}
          className={cn(
            'flex flex-1 items-center justify-center gap-1.5 py-2 text-xs font-semibold transition-colors disabled:opacity-60',
            info.visualizavel && 'border-l border-border',
            'text-primary hover:bg-muted'
          )}
        >
          {baixando ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}{' '}
          Baixar
        </button>
      </div>
    </div>
  );
}

export function MessageMedia({ att, outgoing, messageId, onAbrir }: Props) {
  const info = infoDoArquivo(att);

  if (!att.data_url) {
    if (att.pending) {
      return (
        <Aviso
          outgoing={outgoing}
          icone={<Loader2 size={15} className="shrink-0 animate-spin" />}
          texto={
            att.file_name ? `Recebendo ${att.file_name}…` : `Recebendo ${info.nome.toLowerCase()}…`
          }
        />
      );
    }
    if (att.unavailable) {
      return (
        <Aviso
          outgoing={outgoing}
          icone={<ImageOff size={15} className="shrink-0" />}
          texto={`${info.nome} indisponível. Peça para o cliente mandar de novo.`}
        />
      );
    }
    return null;
  }

  if (info.tipo === 'imagem')
    return <Imagem url={att.data_url} nome={info.nome} onAbrir={onAbrir} />;
  if (info.tipo === 'video') return <Video url={att.data_url} nome={info.nome} onAbrir={onAbrir} />;
  if (info.tipo === 'audio') {
    return (
      <div className="w-[280px] max-w-full">
        <AudioPlayer url={att.data_url} outgoing={outgoing} />
      </div>
    );
  }
  return <Documento att={att} outgoing={outgoing} messageId={messageId} onAbrir={onAbrir} />;
}
